<template>
  <n-modal
    :show="show"
    preset="card"
    class="submit-workspace-dialog"
    style="width: min(520px, calc(100vw - 32px))"
    :auto-focus="false"
    tabindex="-1"
    :bordered="false"
    @close="emit('cancel')"
    @after-enter="focusNaiveModalCardAfterEnter"
    @mask-click="emit('cancel')"
    @esc="emit('cancel')"
  >
    <template #header>
      <div class="submit-workspace-dialog__header">
        <span class="submit-workspace-dialog__title">提交更改</span>
        <span class="submit-workspace-dialog__branch">分支 {{ normalizedBranchName }}</span>
      </div>
    </template>

    <div class="submit-workspace-dialog__body">
      <p class="submit-workspace-dialog__summary">
        将当前工作区改动提交到分支 <strong>{{ normalizedBranchName }}</strong>
      </p>

      <n-form
        label-placement="top"
        size="small"
      >
        <n-form-item
          label="提交信息"
          required
        >
          <n-input
            v-model:value="draftMessage"
            type="textarea"
            placeholder="输入本次提交信息"
            :autosize="{ minRows: 4, maxRows: 6 }"
            maxlength="200"
            show-count
            clearable
            @keydown.ctrl.enter.prevent="confirmSubmit"
          />
        </n-form-item>
      </n-form>
    </div>

    <template #footer>
      <div class="submit-workspace-dialog__footer">
        <n-button
          size="small"
          @click="emit('cancel')"
        >
          取消
        </n-button>
        <n-button
          size="small"
          type="primary"
          :loading="submitting"
          :disabled="confirmDisabled"
          @click="confirmSubmit"
        >
          提交
        </n-button>
      </div>
    </template>
  </n-modal>
</template>

<script setup lang="ts">
/**
 * 工作区提交确认弹框。
 *
 * 说明：提交动作会改变工作区版本状态，因此入口统一要求用户填写提交信息；
 * 真实提交接口尚未接入时，也先把提交信息校验与交互收口在同一个组件中。
 */
import { computed, ref, watch } from 'vue';
import {
  NButton,
  NForm,
  NFormItem,
  NInput,
  NModal,
} from 'naive-ui';
import { focusNaiveModalCardAfterEnter } from '@/hooks/useNaiveModalCardFocus';

const props = withDefaults(defineProps<{
  /** 是否显示弹框。 */
  show: boolean;
  /** 当前提交目标分支名。 */
  branchName?: string | null;
  /** 是否正在提交。 */
  submitting?: boolean;
}>(), {
  branchName: 'main',
  submitting: false,
});

const emit = defineEmits<{
  /** 确认提交，并传出已去除首尾空白的提交信息。 */
  confirm: [message: string];
  /** 取消提交。 */
  cancel: [];
}>();

const draftMessage = ref('');

const normalizedBranchName = computed(() => props.branchName?.trim() || 'main');
const normalizedMessage = computed(() => draftMessage.value.trim());
const confirmDisabled = computed(() => props.submitting || normalizedMessage.value.length === 0);

watch(
  () => props.show,
  (visible) => {
    if (!visible) return;
    draftMessage.value = '';
  },
);

/**
 * 确认提交。
 */
function confirmSubmit(): void {
  if (confirmDisabled.value) return;
  emit('confirm', normalizedMessage.value);
}
</script>

<style scoped>
.submit-workspace-dialog {
  --submit-dialog-surface: var(--n-card-color, var(--n-modal-color, var(--n-color)));
  --submit-dialog-soft-surface: color-mix(in srgb, var(--submit-dialog-surface) 90%, var(--n-primary-color) 7%);
  --submit-dialog-border: color-mix(in srgb, var(--n-border-color) 76%, transparent);
  --submit-dialog-muted: var(--n-text-color-3);
  --submit-dialog-accent: var(--n-primary-color);
}

.submit-workspace-dialog:focus {
  outline: none;
}

.submit-workspace-dialog__header {
  display: flex;
  min-width: 0;
  gap: 12px;
  align-items: center;
}

.submit-workspace-dialog__title {
  flex: 0 0 auto;
}

.submit-workspace-dialog__branch {
  min-width: 0;
  overflow: hidden;
  color: var(--submit-dialog-muted);
  font-size: 12px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.submit-workspace-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.submit-workspace-dialog__summary {
  position: relative;
  margin: 0;
  padding: 0;
  color: var(--n-text-color-2);
  font-size: 13px;
  line-height: 1.55;
}

.submit-workspace-dialog__summary::before {
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: -12px;
  width: 3px;
  border-radius: 999px;
  background: var(--submit-dialog-accent);
  content: '';
}

.submit-workspace-dialog__summary strong {
  color: var(--n-text-color);
  font-weight: 600;
}

.submit-workspace-dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
